import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { from, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { AuthService } from 'continity-shared-shell';

/**
 * Phoenix roles (PBI 1467). The dedicated Phoenix permission set is tracked by
 * PBI 1558 on the backend; until it lands, the board API itself gates on
 * `RequireCompanyAdmin` (see PhoenixBoardController), which is why CompanyAdmin
 * bypasses the role check here.
 *
 * Canonical title strings emitted by the backend: 'PhoenixReader' /
 * 'PhoenixOperator' / 'PhoenixAdmin' (lowest tier is Reader, NOT Viewer).
 */
export const PHOENIX_ROLES = {
  Admin: 'PhoenixAdmin',
  Operator: 'PhoenixOperator',
  Reader: 'PhoenixReader',
} as const;

const PHOENIX_ROLE_VALUES: readonly string[] = [
  PHOENIX_ROLES.Admin,
  PHOENIX_ROLES.Operator,
  PHOENIX_ROLES.Reader,
];

/** CompanyAdmin bypasses the Phoenix-role requirement (PBI 1467). */
const BYPASS_ROLE = 'CompanyAdmin';

/**
 * Roles allowed to see and edit the company profile (`/settings`).
 *
 * CompanyAdmin ONLY, and not as a UI preference: `PUT /api/companies/{id}` is gated
 * `Permissions.Tenants.Edit`, which — per `RolePermissions.cs` — no Phoenix role carries
 * (PhoenixReader/Operator/Admin get Phoenix permissions only). Showing the form to a
 * Phoenix-role user would produce a 403 on save, so the gate belongs in front of the page.
 */
export function isCompanyAdmin(roles: readonly string[]): boolean {
  return roles.includes(BYPASS_ROLE);
}

/** Shared with the authenticated shell so unresolved entitlement never opens a hub connection. */
export function hasPhoenixAccessRole(roles: readonly string[]): boolean {
  return isCompanyAdmin(roles) || roles.some((role) => PHOENIX_ROLE_VALUES.includes(role));
}

/**
 * License gate for Phoenix.
 *
 * STUB (ADO #1524 — FE License-Gate an echtes Entitlement/SKU): there is NO Phoenix
 * license SKU / entitlement field on the subscription contract yet (LicenseLimitsDto
 * has no `phoenix` flag, and the board API gates only on role). So the license
 * predicate is hard-coded `true` for now — the EFFECTIVE gate today is the role check
 * below + the controller's RequireCompanyAdmin. When the Phoenix entitlement lands
 * (ADO #1524), replace this stub with the real subscription/SKU read.
 */
function hasPhoenixLicense(): boolean {
  // STUB: always-true license check — replace with real entitlement read (ADO #1524).
  return true;
}

/**
 * `canAccessPhoenix()` (PBI 1467): authenticated AND
 *   (has a Phoenix role OR is CompanyAdmin) AND has the Phoenix license.
 * Unauthenticated → redirect to landing (which initiates/explains login);
 * authenticated-but-unentitled → redirect to the landing's no-access view.
 */
export const canAccessPhoenix: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  return from(auth.waitForAuthInit()).pipe(
    switchMap(() => {
      if (!auth.isAuthenticated) {
        return of(router.parseUrl('/'));
      }
      if (!hasPhoenixLicense()) {
        return of(router.parseUrl('/no-access'));
      }
      return auth.getUserRoles().pipe(
        map((roles) => {
          const allowed = hasPhoenixAccessRole(roles);
          return allowed ? true : router.parseUrl('/no-access');
        })
      );
    }),
    catchError(() => of(router.parseUrl('/')))
  );
};

/**
 * `canAdministerCompany()`: authenticated AND CompanyAdmin — the gate for `/settings`
 * (see {@link isCompanyAdmin} for why no Phoenix role qualifies). Runs AFTER
 * {@link canAccessPhoenix} on the route, so an unentitled user is already handled; a
 * Phoenix-role user without CompanyAdmin lands on the no-access view rather than on a form
 * whose save is guaranteed to 403.
 */
export const canAdministerCompany: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  return from(auth.waitForAuthInit()).pipe(
    switchMap(() => {
      if (!auth.isAuthenticated) {
        return of(router.parseUrl('/'));
      }
      return auth
        .getUserRoles()
        .pipe(map((roles) => (isCompanyAdmin(roles) ? true : router.parseUrl('/no-access'))));
    }),
    catchError(() => of(router.parseUrl('/')))
  );
};
